/**
 * Camada 1 · o CONTRATO da Central de Ajuda.
 *
 * ===========================================================================
 * DE ONDE ISTO VEM, e o que NAO atravessou
 * ===========================================================================
 * Do manual de casos de uso de 01/09/2026, que descreve 21 passos em 9 casos. Este arquivo
 * cobre os NOVE PASSOS DO USUARIO (UC-06 a UC-09, passos 13 a 21). Os doze do administrador
 * moram no `apps/admin` e sao outra rodada, por decisao dele em 03/09/2026.
 *
 * O VOCABULARIO E GENERALIZADO, e isso foi pedido explicitamente: *"NAO TEM TERÁ NADA A VER
 * COM MEDICINA"*. Entao aqui nao existe paciente, medico, clinica nem prontuario. Existe
 * PESSOA que pergunta e EQUIPE que responde, e os dois servem a qualquer produto da
 * plataforma.
 *
 * ===========================================================================
 * POR QUE INTERFACE, e nao chamada direta
 * ===========================================================================
 * Mesma razao que ja governa `FonteAjuda` em `index.ts`: a suite exercita a tela inteira
 * sem subir `services/knowledge` nem `services/messaging`. E, medido em 03/09/2026, isso
 * deixou de ser conveniencia e virou necessidade: as rotas de conversa NAO EXISTEM nesta
 * plataforma. O legado tem 14; aqui ha 2 GET de artigo. Construir a tela contra o contrato
 * permite valida-la hoje e ligar as rotas depois sem tocar no desenho.
 */

import type { Idioma } from '@complexity/i18n';
import type { ArtigoAjuda } from './index.js';

/**
 * Uma categoria da base, como a Central precisa dela.
 *
 * A CONTAGEM VEM JUNTA porque o manual (passo 03) diz que a categoria *"nasce vazia e so
 * aparece para o usuario quando tiver artigo"*. Sem o numero aqui, a tela do usuario nao
 * teria como cumprir isso, e mostraria categoria vazia que abre em lista vazia.
 */
export interface CategoriaAjuda {
  readonly id: string;
  /** O nome pelo qual a pessoa procura. Passo 02 do manual. */
  readonly nome: string;
  /** Quantos artigos PUBLICADOS ela tem. Zero significa que ela nao aparece. */
  readonly artigos: number;
  /** O idioma do NOME que veio, pela mesma regra de `ArtigoAjuda.idioma` (ADR 0060): pedido
   *  `en` sem traducao cadastrada volta o nome original e `pt` aqui. Opcional pelo mesmo
   *  motivo: a fonte em memoria so tem o original. */
  readonly idioma?: Idioma;
}

/**
 * O tipo da conversa, na classificacao que o passo 17 exige.
 *
 * OS VALORES SAO EM PORTUGUES, e nao os `question`/`support` do legado. Aqui nao ha
 * contrato de backend em ingles a respeitar: o servico ainda nao existe, e nascer com o
 * vocabulario da casa evita a traducao permanente que o legado carrega.
 */
export type TipoDeConversa = 'duvida' | 'suporte' | 'reclamacao' | 'elogio' | 'sugestao';

/** O rotulo de cada tipo, para a tela nao guardar texto solto. */
export const ROTULO_DO_TIPO: Readonly<Record<TipoDeConversa, string>> = Object.freeze({
  duvida: 'Dúvida',
  suporte: 'Suporte',
  reclamacao: 'Reclamação',
  elogio: 'Elogio',
  sugestao: 'Sugestão',
});

/**
 * A situacao de uma conversa, do ponto de vista de quem abriu.
 *
 * SAO TRES E NAO QUATRO: o manual (passo 19) diz *"em analise, respondida, encerrada"*. O
 * legado tem um quarto estado, `answered`, que significa "voce respondeu de volta", e ele
 * NAO entra: da cadeira da pessoa, uma conversa em que ela acabou de escrever esta em
 * analise de novo, e inventar um quarto rotulo so a faria perguntar o que ele quer dizer.
 */
export type SituacaoDaConversa = 'em_analise' | 'respondida' | 'encerrada';

/** O rotulo de cada situacao, no texto exato que o manual mostra na tela. */
export const ROTULO_DA_SITUACAO: Readonly<Record<SituacaoDaConversa, string>> = Object.freeze({
  em_analise: 'Em análise',
  respondida: 'Respondida',
  encerrada: 'Encerrada',
});

/**
 * Um turno da conversa: quem escreveu e o que disse.
 *
 * O AUTOR E `pessoa` OU `equipe`, e nao o nome de quem escreveu. Duas razoes: o nome do
 * atendente nao interessa a quem perguntou, e expor identidade de quem responde cria
 * expectativa de falar com aquela pessoa de novo.
 */
export interface TurnoDaConversa {
  readonly id: string;
  readonly autor: 'pessoa' | 'equipe';
  readonly texto: string;
  /** ISO 8601. A tela formata; o contrato nao decide formato de data. */
  readonly em: string;
}

/**
 * Uma conversa em "Minhas solicitações" (passos 19 e 20).
 *
 * OS TURNOS VEM JUNTOS, e nao por uma segunda chamada. O manual e explicito: *"a conversa e
 * um fio unico, com os turnos em ordem"*, e a lista ja abre mostrando o fio. Buscar turno
 * por conversa faria N chamadas para desenhar uma tela.
 */
export interface ConversaAjuda {
  readonly id: string;
  /** O que a pessoa escreveu em ASSUNTO. Passo 18. */
  readonly assunto: string;
  readonly tipo: TipoDeConversa;
  /** O nome da categoria escolhida, ou `null` quando ela nao escolheu nenhuma. */
  readonly categoria: string | null;
  readonly situacao: SituacaoDaConversa;
  readonly em: string;
  readonly turnos: readonly TurnoDaConversa[];
}

/**
 * O que a pessoa preenche para abrir conversa (passos 17 e 18).
 *
 * `categoriaId` E `categoriaSugerida` SAO EXCLUDENTES, e e isso que faz o passo 21 existir:
 * ou ela escolhe uma da lista, ou escreve o nome de uma que falta. Um campo unico obrigaria
 * quem le a adivinhar se o texto e um id ou uma sugestao.
 */
export interface NovaConversa {
  readonly tipo: TipoDeConversa;
  readonly assunto: string;
  readonly mensagem: string;
  /** A categoria escolhida da lista. `null` quando ela sugeriu uma nova, ou nao escolheu. */
  readonly categoriaId: string | null;
  /** O nome da categoria que faltou (passo 21). `null` quando ela escolheu da lista. */
  readonly categoriaSugerida: string | null;
}

/**
 * O contrato COMPLETO da Central de Ajuda.
 *
 * ELE ESTENDE `FonteAjuda` em vez de substitui-lo: o `PainelAjuda` que ja existe consome
 * `FonteAjuda`, e quebrar aquele contrato quebraria o painel do prontidao junto. Quem so
 * precisa de busca continua implementando `FonteAjuda`; quem quer a Central inteira
 * implementa este.
 */
/**
 * Uma imagem que vai junto da mensagem.
 *
 * O `base64` VEM SEM O PREFIXO `data:`, porque e assim que a rota do messaging o espera
 * (services/messaging/src/conversas/conversas.controller.ts:298). Quem produz este objeto
 * e `reduzirImagemParaAnexo` de @complexity/ui, que ja reduz a imagem ate caber.
 */
export interface ArquivoParaAnexar {
  readonly nomeArquivo: string;
  readonly tipoConteudo: string;
  readonly base64: string;
}

/**
 * Um anexo como ele aparece NA CONVERSA, e sem o conteudo.
 *
 * O conteudo vem por `anexo(...)`, um por vez, quando a pessoa CLICA: trazer o arquivo de
 * todos so para desenhar nomes faria cada abertura de conversa carregar megabytes que
 * ninguem vai olhar.
 */
export interface AnexoDaConversa {
  readonly id: string;
  readonly mensagemId: string;
  readonly nomeArquivo: string;
  readonly tipoConteudo: string;
  readonly tamanhoBytes: number;
}

export interface FonteCentral {
  /*
   * OS QUATRO DE CONHECIMENTO RECEBEM `idioma` (ADR 0060), e as tres de conversa NAO: a
   * conversa e texto escrito pela propria pessoa, e esse continua na lista de `NAO_TRADUZIR`.
   * Opcional pelo mesmo motivo de `FonteAjuda`: ausente, a fonte HTTP usa o idioma corrente.
   */
  /** Passo 15: as categorias, ja sem as vazias. */
  categorias(namespace: string, idioma?: Idioma): Promise<readonly CategoriaAjuda[]>;
  /** Passo 14: a busca, que casa com marcadores e nao so com o titulo. */
  buscar(namespace: string, termo: string, idioma?: Idioma): Promise<readonly ArtigoAjuda[]>;
  /** Os artigos de uma categoria (passo 15, depois de escolher). */
  artigosDaCategoria(
    namespace: string,
    categoriaId: string,
    idioma?: Idioma,
  ): Promise<readonly ArtigoAjuda[]>;
  /** O corpo de um artigo. Separado da lista: trazer o corpo de todos para desenhar
   *  uma lista de titulos seria carregar o que ninguem vai ler. */
  corpo(namespace: string, artigoId: string, idioma?: Idioma): Promise<string | null>;
  /** Passos 19 e 20: as conversas de quem esta olhando, com o fio inteiro. */
  minhasConversas(namespace: string): Promise<readonly ConversaAjuda[]>;
  /** Passo 18: abre a conversa. Devolve a conversa criada, para a lista nao recarregar. */
  abrirConversa(namespace: string, nova: NovaConversa): Promise<ConversaAjuda>;
  /** Passo 20: escrever de volta no mesmo fio. */
  responder(namespace: string, conversaId: string, texto: string): Promise<ConversaAjuda>;
  /**
   * Anexa imagens a uma MENSAGEM ja existente.
   *
   * ELE E OPCIONAL, e a razao e medida: existem DUAS implementacoes deste contrato, e a
   * segunda e `criarFonteCentralEmMemoria` (fonte-memoria.ts:106), que e a degradacao
   * declarada quando `VITE_CX_API_KNOWLEDGE` falta. Metodo obrigatorio quebraria o
   * typecheck dela, e a tela cairia ANTES de dizer que a configuracao esta incompleta.
   *
   * A TELA CONFERE SE ELE EXISTE antes de oferecer o anexo: sem ele, o campo simplesmente
   * nao aparece, em vez de aparecer e falhar no envio.
   *
   * ANEXA A UMA MENSAGEM, e nao a uma conversa: o anexo pertence ao que foi dito, e nao ao
   * assunto. O id da mensagem sai dos `turnos` da conversa devolvida por `abrirConversa`.
   */
  anexar?(
    namespace: string,
    conversaId: string,
    mensagemId: string,
    arquivos: readonly ArquivoParaAnexar[],
  ): Promise<void>;
  /**
   * OS ANEXOS de uma conversa inteira, sem o conteudo.
   *
   * OPCIONAL pelo mesmo motivo de `anexar`: a fonte EM MEMORIA nao os tem, e obrigatorio
   * quebraria o typecheck dela, que e a degradacao declarada quando falta a variavel de
   * ambiente.
   */
  anexosDaConversa?(namespace: string, conversaId: string): Promise<readonly AnexoDaConversa[]>;
  /** UM anexo COM o conteudo em base64, para a tela abrir. */
  anexo?(
    namespace: string,
    anexoId: string,
  ): Promise<{ nomeArquivo: string; tipoConteudo: string; base64: string }>;
}

/**
 * `ArtigoAjuda` NAO e reexportado daqui, e a razao e o typecheck.
 *
 * A primeira versao tentou `export type { ArtigoAjuda as ArtigoAjudaMinimo }`, e o
 * compilador recusou: reexport NAO cria nome usavel dentro do proprio arquivo. Trocado por
 * `import type` no topo, o reexport passou a colidir com o import. Quem precisa do tipo o
 * pega de `index.js`, que e onde ele nasce, e um nome so continua tendo uma casa so.
 */
